/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";
import Text from "./ui/Text";
import Flex from "./ui/Flex";
import Badge from "./ui/Badge";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import QuantityInput from "./QuantityInput";
import FormatPrice from "./formatters/FormatPrice";
import { useAssetsSrc } from "../hooks/useAssetsSrc";
import { cartRegistry } from "../utils/cartRegistry";
import { CART_LINE_TYPES, CART_LINE_TYPE_LABELS, DISCOUNT_TYPE } from "../constants/constants";
import { useTranslate } from "../hooks/useTranslate";

const ICON_BY_LINE_TYPE = {
  [CART_LINE_TYPES.PRODUCT]: <Icon.Bag />,
  [CART_LINE_TYPES.MEMBERSHIP]: <Icon.MembershipItem />,
  [CART_LINE_TYPES.PROMOTION]: <Icon.Gift />,
  [CART_LINE_TYPES.PACKAGE]: <Icon.BoxArchive color="iconStroke" strokeWidth={0.5}/>,
};

const PROMOTION_ICON_BY_DISCOUNT_TYPE = {
  [DISCOUNT_TYPE.PERCENTAGE]: <Icon.Promotion color="iconStroke" strokeWidth={0.5} />,
  [DISCOUNT_TYPE.FIXED]: <Icon.GiftCard color="iconStroke" strokeWidth={1.3}/>,
};

const resolveLineIcon = (line) => {
  if (line.type === CART_LINE_TYPES.PROMOTION) {
    const discountType = line.snapshot?.discount_type;
    return PROMOTION_ICON_BY_DISCOUNT_TYPE[discountType] ?? ICON_BY_LINE_TYPE[CART_LINE_TYPES.PROMOTION];
  }
  return ICON_BY_LINE_TYPE[line.type];
};

const RowWrapper = styled.div`
  display: grid;
  align-items: center;
  column-gap: 1rem;
  row-gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});

  ${({ $hasThumbnail }) => $hasThumbnail
    ? css`
        grid-template-columns: auto 1fr auto auto auto;
        grid-template-areas: "media name qty price trash";
      `
    : css`
        grid-template-columns: 1fr auto auto auto;
        grid-template-areas: "name qty price trash";
      `}

  &:last-child {
    border-bottom: none;
  }

  ${({ theme }) => theme.containerMedia.sm} {
    align-items: start;

    ${({ $hasThumbnail }) => $hasThumbnail
    ? css`
          grid-template-columns: auto 1fr auto;
          grid-template-areas:
            "media name  trash"
            ".     qty   price";
        `
    : css`
          grid-template-columns: 1fr auto;
          grid-template-areas:
            "name trash"
            "qty  price";
        `}
  }
`;

const ItemMedia = styled(Flex)`
  grid-area: media;
  position: relative;
  width: 62px;
  height: 48px;
  border-radius: 0.5rem;
  flex-shrink: 0;
  overflow: hidden;

  ${({ theme }) => theme.containerMedia.sm} {
    width: 56px;
    height: 56px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemIcon = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
`;

const ItemPlaceholder = styled(Flex)`
  position: relative;
  width: 62px;
  height: 48px;
  border-radius: 0.5rem;
  background-color: rgba(${({ theme }) => theme.colors.border.rgb}, 0.5);
`;

const NameBlock = styled(Flex)`
  grid-area: name;
  min-width: 0;
  align-self: center;
`;

const QtyCell = styled.div`
  grid-area: qty;
  justify-self: start;
  margin-left: -0.5rem;

  ${({ theme }) => theme.containerMedia.sm} {
    margin-left: 0;
  }
`;

const PriceText = styled(Text)`
  grid-area: price;
  justify-self: end;
  align-self: center;
  min-width: 70px;
  text-align: right;
  word-break: normal;
`;

const TrashCell = styled.div`
  grid-area: trash;
  justify-self: end;
`;

const CartLineRow = ({
  line,
  showThumbnail = false,
  showTypeBadge = false,
  onQtyChange,
  onRemove,
}) => {
  const { _t } = useTranslate();
  const resolvedIcon = resolveLineIcon(line) ?? <Icon.Bag />;
  const IconComponent = React.cloneElement(resolvedIcon, { size: Icon.SIZE.LG });
  const item = line.snapshot ?? {};
  const qty = line.params?.qty ?? 1;
  const editable = !!cartRegistry[line.type]?.canEditQty;
  const pictureSrc = useAssetsSrc(item.picture);
  const registryEntry = cartRegistry[line.type];
  const typeLabel = CART_LINE_TYPE_LABELS[line.type];
  const metadata = registryEntry?.renderMetadata?.(item) ?? null;

  const handleStepperChange = (newQty) => {
    if (newQty <= 0) {
      onRemove?.(line);
      return;
    }
    onQtyChange?.(line, newQty);
  };

  const renderMedia = () => {
    if (!showThumbnail) return null;

    if (item.picture) {
      return item.picture.endsWith(".svg") ? (
        <ItemIcon as="object" data={pictureSrc} type="image/svg+xml" />
      ) : (
        <ItemImage src={pictureSrc} alt={item.name} />
      );
    }

    return (
      <ItemPlaceholder $align="center" $justify="center">
        {IconComponent}
      </ItemPlaceholder>
    );
  };

  return (
    <RowWrapper $hasThumbnail={showThumbnail}>
      {showThumbnail && (
        <ItemMedia $justify="center" $align="center">
          {renderMedia()}
        </ItemMedia>
      )}

      <NameBlock $direction="column" $gap="0.25rem" $align="start">
        <Text color="dark" weight="bold" size="medium" lineClamp={2}>
          {item.name}
        </Text>
        {((showTypeBadge && typeLabel) || metadata) && (
          <Flex $align="center" $gap="0.25rem" $wrap="wrap">
            {showTypeBadge && typeLabel && <Badge title={typeLabel} color="neutral" />}
            {metadata && <Text size="small">{metadata}</Text>}
          </Flex>
        )}
      </NameBlock>

      {editable && (
        <QtyCell>
          <QuantityInput
            value={qty}
            onUpdate={handleStepperChange}
          />
        </QtyCell>
      )}

      <PriceText color="dark" weight="bold" size="large">
        <FormatPrice value={(item.price ?? 0) * qty} currency={item.currency} />
      </PriceText>

      <TrashCell>
        <Button
          icon={<Icon.Trash color="iconStroke"/>}
          variant="transparent"
          size="icon"
          onClick={() => onRemove?.(line)}
          aria-label={_t("Remove item")}
        />
      </TrashCell>
    </RowWrapper>
  );
};

export default CartLineRow;
